import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  SOFT_BADGE_CLASSES,
  SkeletonTable,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@mustauto/ui/web';
import { INSURER_RECEIVABLE_STATUS_LABELS, type InsurerReceivableStatus } from '@mustauto/types';
import { formatDateBR, formatReais } from '@mustauto/utils';
import { api } from '@/lib/api';

interface InsurerReceivableItem {
  osId: string;
  number: number;
  plate: string;
  vehicleName: string;
  osStatus: string;
  insurerLabel: string;
  claimNumber: string | null;
  insurerDue: number;
  insurerPaid: number;
  insurerRemaining: number;
  dueDate: string | null;
  status: InsurerReceivableStatus;
}

interface InsurerReceivablesPayload {
  summary: {
    remaining: number;
    overdue: number;
    awaitingService: number;
    byInsurer: { label: string; remaining: number; count: number }[];
  };
  items: InsurerReceivableItem[];
}

const STATUS_BADGE: Record<InsurerReceivableStatus, string> = {
  nothing_due: SOFT_BADGE_CLASSES.gray,
  awaiting_service: SOFT_BADGE_CLASSES.slate,
  open: SOFT_BADGE_CLASSES.indigo,
  partial: SOFT_BADGE_CLASSES.amber,
  overdue: SOFT_BADGE_CLASSES.red,
  paid: SOFT_BADGE_CLASSES.emerald,
};

/**
 * Financeiro → "A receber de seguradoras" (Fase 4). O outro devedor da OS de
 * sinistro: o cliente pagou a franquia e levou o carro; a seguradora paga o
 * resto em 30 a 60 dias. Contas a Receber, logo acima, segue sendo só do
 * cliente.
 *
 * Só aparece quando há algum sinistro com saldo — oficina que não trabalha com
 * seguradora não vê a seção. O pagamento se registra no sinistro, dentro da OS.
 */
export function InsurerReceivablesSection() {
  const { data, isLoading, isError, error, refetch } = useQuery<InsurerReceivablesPayload>({
    queryKey: ['finance', 'insurer-receivables'],
    queryFn: async () => (await api.get('/workshops/my/finance/insurer-receivables')).data,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <SkeletonTable rows={2} />
        </CardContent>
      </Card>
    );
  }
  if (isError && !data) {
    return (
      <Card>
        <CardContent className="pt-6">
          <ErrorState error={error} onRetry={() => refetch()} />
        </CardContent>
      </Card>
    );
  }
  if (!data || data.items.length === 0) return null;

  const { summary } = data;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
          <ShieldCheck size={16} aria-hidden />
          A receber de seguradoras
        </CardTitle>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-semibold tabular-nums">{formatReais(summary.remaining)} em aberto</span>
          {summary.overdue > 0 && (
            <span className="font-medium tabular-nums text-red-700 dark:text-red-300">{formatReais(summary.overdue)} vencido</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {summary.byInsurer.length > 1 && (
          <div className="flex flex-wrap gap-2 text-xs">
            {summary.byInsurer.map((b) => (
              <span key={b.label} className="rounded-md border px-2 py-1">
                {b.label}: <span className="font-semibold tabular-nums">{formatReais(b.remaining)}</span>
                <span className="text-muted-foreground"> · {b.count} {b.count === 1 ? 'sinistro' : 'sinistros'}</span>
              </span>
            ))}
          </div>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Seguradora</TableHead>
                <TableHead>OS</TableHead>
                <TableHead>Veículo</TableHead>
                <TableHead className="text-right">Falta</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Situação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item) => (
                <TableRow key={item.osId}>
                  <TableCell>
                    {item.insurerLabel}
                    {item.claimNumber && <span className="block font-mono text-xs text-muted-foreground">{item.claimNumber}</span>}
                  </TableCell>
                  <TableCell>
                    <Link to="/service-orders/$osId" params={{ osId: item.osId }} className="font-medium text-primary hover:underline">
                      #{item.number}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs font-semibold">{item.plate}</span>
                    <span className="block text-xs text-muted-foreground">{item.vehicleName}</span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatReais(item.insurerRemaining)}
                    {item.insurerPaid > 0 && (
                      <span className="block text-xs text-muted-foreground">de {formatReais(item.insurerDue)}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{item.dueDate ? formatDateBR(item.dueDate) : '—'}</TableCell>
                  <TableCell>
                    <span className={`whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[item.status]}`}>
                      {INSURER_RECEIVABLE_STATUS_LABELS[item.status]}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {summary.awaitingService > 0 && (
          <p className="text-xs text-muted-foreground">
            {formatReais(summary.awaitingService)} de carros ainda na oficina: sem vencimento combinado, vence 30 dias depois da
            conclusão da OS.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
